/**
 * decorationSections.js -- Store Decorations C1 redesign (ADR 0064).
 *
 * The ONE partition + search + work-strip counting for the redesigned
 * Store Decorations page (StoreDecorations.vue, C1 direction). Every list
 * control -- the work strip, the three section headers, and the rail --
 * counts over the functions here, so the strip and the headers can never
 * disagree about which decorations need the operator (README "Sections and
 * search").
 *
 * Ported from the design source's sd-data.jsx partition logic
 * (planning/design-handoffs/handoff_2026-08-03/2026-08-03-store-decorations/
 * design/sd-data.jsx: sectionOf / rollupState / matchesQuery / SEVERITY),
 * keyed on the backend's underscore lifecycle wire format (the JSX used
 * hyphens) and the real rollup response shape from
 * GET /api/stores/<id>/decoration_rollup/ (each placement carries
 * placement_zone {id, slug, display_name, zone_class}).
 *
 * Pure functions, no Vue -- unit-tested directly and shared between the
 * section body, the rail, and the work strip so there is one source for the
 * partition.
 *
 * PLACEHOLDER SEAM (pebble 3b): `hasFitIssue` currently returns false for
 * every decoration. Fit verdicts require the on-garment preview machinery
 * (silhouette hotspots + zone printable dimensions + compute_verdict), which
 * lands in pebble 3b. Until then a decoration lands in "Needs you" on its
 * lifecycle state alone. When 3b wires the verdict path, fill in
 * `hasFitIssue` (a placement whose verdict is not `fit`) and the partition
 * updates everywhere at once with no other change -- `sectionOf` already
 * ORs it in, matching the locked "a fit problem is work whatever the
 * approval says" rule (README, Locked Decision 6a).
 */

import { isReady, needsAttention } from "./decorationLifecycle";
import {
  computePlacementVerdict,
  zoneInchesFor,
} from "@/components/decoration/placementPreviewGeometry";

// Severity order for the worst-of rollup state, ported verbatim from
// sd-data.jsx SEVERITY (underscore-keyed). The FIRST match wins, so
// changes_requested outranks awaiting_client_approval outranks the
// digitization/draft states, then the ready states, then archived.
export const SEVERITY = Object.freeze([
  "changes_requested",
  "awaiting_client_approval",
  "awaiting_digitization",
  "in_digitization",
  "draft",
  "approved",
  "in_use",
  "archived",
]);

// The three sections, in order. `tone` drives the GroupBar color family
// (warn = amber, ok = green, idle = neutral). Ported from sd-data.jsx
// SECTIONS. "Not on a product yet" (available) starts collapsed -- the
// component owns the initial collapsed map, this list just names the keys.
export const SECTIONS = Object.freeze([
  { key: "needs", label: "Needs you", tone: "warn" },
  { key: "ready", label: "Approved & in use", tone: "ok" },
  { key: "available", label: "Not on a product yet", tone: "idle" },
]);

/**
 * Placements whose per-placement override still needs work. Once
 * per-placement size overrides land (pebble 3b/3d), a placement can carry
 * its own lifecycle state that differs from the decoration's. Today the
 * rollup never returns overrides, so this is [] -- but rollupState reads it
 * so the shape is right when overrides arrive.
 */
function overrideStates(deco) {
  const placements = Array.isArray(deco.placements) ? deco.placements : [];
  return placements
    .map((p) => p && p.override && p.override.state)
    .filter(Boolean);
}

/**
 * Worst state across the decoration and any of its placement overrides.
 * This is what groups the row and colors its chip, so one decoration is
 * one item in "Needs you" however many of its placements are mid-flight
 * (README "A decoration is ONE item in Needs-you"). With no overrides it is
 * simply the decoration's own lifecycle_state.
 */
export function rollupState(deco) {
  const states = [deco.lifecycle_state, ...overrideStates(deco)];
  let best = states[0];
  let bestRank = SEVERITY.indexOf(best);
  for (const s of states) {
    const rank = SEVERITY.indexOf(s);
    // A state not in SEVERITY (unexpected backend value) sorts last so it
    // never masks a real state -- treat -1 as the largest rank.
    const norm = rank === -1 ? SEVERITY.length : rank;
    const bestNorm = bestRank === -1 ? SEVERITY.length : bestRank;
    if (norm < bestNorm) {
      best = s;
      bestRank = rank;
    }
  }
  return best;
}

/**
 * The fit verdict for ONE placement of a decoration. Pure arithmetic on
 * inches (decoration width/height x zone printable inches x the target
 * product's size-class scale) -- a faithful mirror of the backend
 * ``compute_verdict``, shared with the on-garment preview (pebble 3b, ADR
 * 0065). The DRAWING (silhouette/hotspot) is NOT needed for the verdict, so
 * this works off the rollup placement alone: ``placement_zone`` (its
 * ``default_max_*`` dims, or the seeded slug fallback) + an optional
 * ``size_scale_factor`` on the placement (youth ~0.78; absent -> adult).
 *
 * Returns the ``{verdict, reason, size_delta_pct}`` shape. A placement with no
 * zone, or a zone with no resolvable printable inches, yields no fit judgement
 * (``null``) -- it is not counted as a fit ISSUE (we don't invent a problem we
 * can't measure), matching the "verdict is exact or absent, never guessed"
 * rule.
 *
 * @param {object} deco       the rollup decoration (width_in / height_in)
 * @param {object} placement  one of deco.placements (placement_zone, scale)
 * @returns {{verdict: string, reason: string, size_delta_pct: number|null}|null}
 */
export function placementVerdict(deco, placement) {
  const zone = placement && placement.placement_zone;
  if (!zone) return null;
  const inches = zoneInchesFor(zone);
  if (!inches) return null;
  const [zw, zh] = inches;
  const scale =
    typeof placement.size_scale_factor === "number"
      ? placement.size_scale_factor
      : 1.0;
  return computePlacementVerdict({
    widthIn: deco.width_in,
    heightIn: deco.height_in,
    zoneWidthIn: zw,
    zoneHeightIn: zh,
    sizeScaleFactor: scale,
  });
}

/**
 * A decoration has a fit issue when ANY of its placements has a computable
 * verdict that is not `fit` (remap / drop). A placement whose verdict cannot
 * be computed (no zone / no dims) is NOT a fit issue -- an unmeasurable
 * placement is not a known problem. Wired in pebble 3b; ``sectionOf`` already
 * ORs it into the "needs you" partition, and the work strip's "Doesn't fit"
 * chip counts over the same predicate, so section and strip cannot disagree
 * (README Locked Decision 6a).
 */
export function hasFitIssue(deco) {
  const placements = Array.isArray(deco.placements) ? deco.placements : [];
  return placements.some((p) => {
    const v = placementVerdict(deco, p);
    // Only a MEASURED misfit (remap / drop) is a fit issue. A `blocked`
    // verdict means the dimensions are missing/invalid (e.g. a decoration
    // without imprint inches) -- unmeasurable, not a known problem -- so it is
    // NOT counted, matching "verdict is exact or absent, never guessed".
    return v !== null && (v.verdict === "remap" || v.verdict === "drop");
  });
}

/**
 * The ONE partition. Placement count FIRST, lifecycle state SECOND
 * (Locked Decision 6a):
 *   - no placements            -> "available" (not on a product yet)
 *   - state needs attention OR a placement doesn't fit -> "needs"
 *   - otherwise                -> "ready" (approved & in use)
 */
export function sectionOf(deco) {
  const placements = Array.isArray(deco.placements) ? deco.placements : [];
  if (!placements.length) return "available";
  if (needsAttention(rollupState(deco)) || hasFitIssue(deco)) return "needs";
  return "ready";
}

/**
 * Type-down search over the things staff know a decoration by: its own name,
 * file and method, and -- because "what's on the coaches polo?" / "what's on
 * K500?" is the more common question -- the store listing name, supplier SKU
 * and zone of every product it is placed on (README "Type-down search").
 *
 * `nameFor` de-GUIDs the decoration name the SAME way the row renders it, so
 * a search matches what the operator sees. Supplier SKU is matched from the
 * `supplier_sku` field the rollup emits on each placement (decoration_view.py,
 * sourced from cp.product_item_number).
 */
export function matchesQuery(deco, query, nameFor) {
  const n = String(query || "").trim().toLowerCase();
  if (!n) return true;
  const placements = Array.isArray(deco.placements) ? deco.placements : [];
  const parts = [
    typeof nameFor === "function" ? nameFor(deco) : deco.artwork_name,
    deco.artwork_original_filename,
    deco.artwork_filename,
    deco.method && deco.method.display_name,
  ];
  for (const p of placements) {
    parts.push(p.product_name);
    // Supplier SKU, as the rollup emits it (cp.product_item_number).
    parts.push(p.supplier_sku);
    if (p.placement_zone) {
      parts.push(p.placement_zone.display_name);
      parts.push(p.placement_zone.slug);
    }
  }
  const hay = parts.filter(Boolean).join("  ").toLowerCase();
  return hay.includes(n);
}

export default {
  SEVERITY,
  SECTIONS,
  rollupState,
  placementVerdict,
  hasFitIssue,
  sectionOf,
  matchesQuery,
};
